import React, { useEffect, useState } from 'react';
import { useEPMContext } from '../../../contexts/EPMContext';
import { formatTestCardId } from '../../../utils/converter';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TestOverview from '../TestOverview';
import { stepForStatus } from '../epmHelpers';
import Modal from '../../UI/Modal/Modal';
import Button from '../../UI/Button/Button';

export const TestPeekModal = ({ testId, onClose }) => {
    const { fetchTestById } = useEPMContext();
    const [testData, setTestData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDetails = async () => {
            setIsLoading(true);
            try {
                const data = await fetchTestById(testId);
                setTestData(data);
            } catch (err) {
                toast.error("Failed to load test details.");
                onClose();
            } finally {
                setIsLoading(false);
            }
        };
        fetchDetails();
    }, [testId, fetchTestById, onClose]);

    useEffect(() => {
        const handleEsc = (e) => {
            // An open screenshot lightbox takes the first Escape
            if (e.key === 'Escape' && !document.querySelector('.epm-shot-lightbox, .epm-step-lightbox')) onClose();
        };
        window.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'auto';
        };
    }, [onClose]);

    const handleCopyLink = () => {
        const link = `${window.location.origin}/epm/test/details/${testId}`;
        navigator.clipboard.writeText(link);
        toast.success("Link copied to clipboard!");
    };

    // Open full test goes to the step the test is at (storyboard 10)
    const handleOpenFullTest = () => {
        navigate(`/epm/test-workflow/${testId}?step=${stepForStatus(testData?.status)}`);
    };

    if (isLoading) {
        return (
            <Modal isOpen={true} onClose={onClose} size="xl" className="overflow-hidden p-0">
                <div className="flex justify-center items-center h-64">
                    <span className="text-gray-400 text-sm font-medium">Loading test details...</span>
                </div>
            </Modal>
        );
    }

    if (!testData) return null;

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            size="xl"
            ariaLabel={`${formatTestCardId(testData)} ${testData?.name}`}
            className="overflow-hidden p-0"
        >
            <TestOverview
                testData={testData}
                setTestData={setTestData}
                variant="peek"
                actions={(
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="xs" onClick={handleCopyLink}>Copy link</Button>
                        <Button variant="outline" size="xs" onClick={handleOpenFullTest}>Open full test ↗</Button>
                        <Button variant="ghost" size="xs" onClick={onClose} aria-label="Close modal">✕</Button>
                    </div>
                )}
            />
        </Modal>
    );
};

export default TestPeekModal;
